import React, { useState, useEffect, useMemo } from 'react';
import { DillButton, DillCheckbox, DillModal } from '../../../general/Dill';
import { AppInputField } from '../../../general/AppInputField/AppInputField';
import { CreditApplication, CreditApplicationFieldsValues, formatMoney } from '@dill/dill-shared';
import { twMerge } from 'tailwind-merge';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { sendFeedbackCreditApplication } from '../../../../redux/services/creditApplicationsService';
import { openDillToast } from '../../../../utils/helpers';
import moment from 'moment';
import SpecificRulesSelector, { SpecificRule } from './SpecificRulesSelector';

type GroupedField = {
  groupName: string;
  groupedPosition: number;
  items: CreditApplicationFieldsValues[];
};

type StepFields = (CreditApplicationFieldsValues | GroupedField)[];

const CreditApplicationFeedbackModal = ({
  open,
  handleClose,
  creditApplication,
  applicationHistoryId,
}: {
  open: boolean;
  handleClose: () => void;
  creditApplication: CreditApplication | null;
  applicationHistoryId?: string | null;
}) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const { creditApplicationTemplates } = useAppSelector(
    (state) => state.creditApplicationTemplates
  );

  const [feedback, setFeedback] = useState('');
  const [errorFields, setErrorFields] = useState<string[]>([]);
  const [selectedFields, setSelectedFields] = useState<
    { name?: string; uniqueId: string; id: string; onlyShowPlaidOption?: boolean }[]
  >([]);
  const [sendingFeedback, setSendingFeedback] = useState(false);
  const [specificRules, setSpecificRules] = useState<SpecificRule[]>([]);

  const templateForApplication = useMemo(() => {
    if (!creditApplication?.templateId) return null;
    return creditApplicationTemplates.find((t) => t.id === creditApplication.templateId) ?? null;
  }, [creditApplication, creditApplicationTemplates]);

  useEffect(() => {
    if (open) {
      setFeedback('');
      setErrorFields([]);
      setSelectedFields([]);
      setSpecificRules(creditApplication?.specificRules ?? []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function groupByStepName(data: CreditApplicationFieldsValues[]) {
    return data.reduce((acc, item) => {
      if (!acc[item.stepName]) acc[item.stepName] = [];

      if (item.groupName) {
        let group = (acc[item.stepName] as StepFields).find(
          (g): g is GroupedField =>
            'groupName' in g &&
            g.groupName === item.groupName &&
            g.groupedPosition === item.groupedPosition
        );

        if (!group) {
          group = {
            groupName: item.groupName,
            groupedPosition: item.groupedPosition,
            items: [],
          };
          acc[item.stepName].push(group);
        }

        group.items.push(item);
      } else {
        acc[item.stepName].push(item);
      }

      return acc;
    }, {} as Record<string, StepFields>);
  }

  const allFields = useMemo(() => {
    const creditApplicationFields = creditApplication?.fieldValues
      ? creditApplication.fieldValues.filter(
          (field) =>
            field.fromField !== 'DOCUMENT_FIELD' &&
            field.fromField !== 'INVITE_USER' &&
            field.id !== 'first-name-personal' &&
            field.id !== 'last-name-personal' &&
            field.id !== 'email-personal'
        )
      : [];

    return groupByStepName(creditApplicationFields.map((field) => field));
  }, [creditApplication, open]);

  const validateInputs = () => {
    const errors: string[] = [];
    if (feedback.trim() === '') errors.push('feedback');
    setErrorFields(errors);
    return errors.length === 0;
  };

  const handleCloseModal = () => {
    setFeedback('');
    setErrorFields([]);
    setSelectedFields([]);
    setSpecificRules([]);
    setSendingFeedback(false);
    handleClose();
  };

  const handleSubmit = async () => {
    if (!validateInputs() || !creditApplication) return;
    setSendingFeedback(true);

    const results = await dispatch(
      sendFeedbackCreditApplication({
        supplierId: currentSupplier?.id || '',
        creditApplicationId: creditApplication.id,
        feedback,
        fields: selectedFields,
        ...(applicationHistoryId ? { applicationHistoryId } : {}),
        ...(specificRules.length > 0 ? { specificRules } : {}),
      })
    );

    if (results.type.endsWith('/fulfilled')) {
      openDillToast({ type: 'SUCCESS', message: 'Feedback sent successfully.' });
      handleCloseModal();
    } else {
      openDillToast({ type: 'ERROR', message: 'Failed to send feedback.' });
    }
    setSendingFeedback(false);
  };

  const hasFields = Object.keys(allFields).length > 0;

  return (
    <DillModal
      open={open}
      onClose={handleCloseModal}
      title="Send Feedback"
      maxWidth="620px"
      footer={
        <div className="flex items-center justify-between w-full gap-3">
          <div className="flex items-center gap-2">
            {errorFields.length > 0 && (
              <p className="text-xs text-red-500">Please fill in the required fields</p>
            )}
            {selectedFields.length > 0 && errorFields.length === 0 && (
              <span className="text-xs text-gray-500">
                {selectedFields.length} field{selectedFields.length !== 1 ? 's' : ''} flagged for
                update
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <DillButton label="Cancel" variant="secondary" onClick={handleCloseModal} />
            <DillButton
              label="Send Feedback"
              onClick={handleSubmit}
              isLoading={sendingFeedback}
              isDisabled={!creditApplication || sendingFeedback}
            />
          </div>
        </div>
      }>
      <div className="flex flex-col gap-5">
        <AppInputField
          isRequired
          label="Message"
          value={feedback}
          placeholder="Describe what needs to be corrected or updated…"
          onTextChange={(val) => {
            setFeedback(val);
            setErrorFields((prev) => prev.filter((f) => f !== 'feedback'));
          }}
          inputType="textarea"
          errorText={errorFields.includes('feedback') ? 'A message is required' : ''}
        />

        {templateForApplication && (
          <SpecificRulesSelector
            templates={[templateForApplication]}
            rules={specificRules}
            onChange={setSpecificRules}
          />
        )}

        {hasFields && (
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-700">Flag fields for update</span>
              <span className="text-xs text-gray-400 font-normal">— optional</span>
            </div>

            <div className="flex flex-col gap-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
              {Object.entries(allFields).map(([stepName, fields]) => (
                <div key={stepName} className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                      {stepName}
                    </span>
                    <div className="flex-1 h-px bg-gray-200" />
                    <span className="text-xs text-gray-400">{fields.length}</span>
                  </div>

                  <div className="grid grid-cols-2 gap-1.5">
                    {fields.map((field, index) => {
                      const isGroup = 'items' in field;

                      if (isGroup) {
                        const isSelected = selectedFields.some(
                          (f) => f.uniqueId === field.items[0].uniqueId
                        );
                        const handleSelect = () => {
                          setSelectedFields((prev) =>
                            isSelected
                              ? prev.filter((f) => f.uniqueId !== field.items[0].uniqueId)
                              : [
                                  ...prev,
                                  {
                                    name: field.items[0].name,
                                    uniqueId: field.items[0].uniqueId,
                                    id: field.items[0].id,
                                    ...(field.items[0].onlyShowPlaidOption && {
                                      onlyShowPlaidOption: field.items[0].onlyShowPlaidOption,
                                    }),
                                  },
                                ]
                          );
                        };
                        return (
                          <div
                            key={field.items[0].uniqueId + index}
                            className={twMerge(
                              'flex items-center gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-all select-none',
                              isSelected
                                ? 'border-blue-400 bg-blue-50 shadow-sm'
                                : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                            )}
                            onClick={handleSelect}>
                            <DillCheckbox isChecked={isSelected} />
                            <div className="min-w-0">
                              <div className="text-xs font-medium text-gray-800 truncate">
                                {field.groupName}
                              </div>
                              <div className="text-xs text-gray-400">
                                Entry {field.groupedPosition + 1}
                              </div>
                            </div>
                          </div>
                        );
                      } else {
                        const isSelected = selectedFields.some(
                          (f) => f.uniqueId === field.uniqueId
                        );
                        const plaidChecked =
                          selectedFields.find((f) => f.uniqueId === field.uniqueId)
                            ?.onlyShowPlaidOption ?? false;

                        const handleSelect = () => {
                          setSelectedFields((prev) =>
                            isSelected
                              ? prev.filter((f) => f.uniqueId !== field.uniqueId)
                              : [
                                  ...prev,
                                  {
                                    name: field.name,
                                    uniqueId: field.uniqueId,
                                    id: field.id,
                                    ...(field.onlyShowPlaidOption && {
                                      onlyShowPlaidOption: true,
                                    }),
                                  },
                                ]
                          );
                        };

                        const handleSelectPlaid = (
                          uniqueId: string,
                          onlyShowPlaidOption: boolean
                        ) => {
                          setSelectedFields((prev) =>
                            prev.map((f) =>
                              f.uniqueId === uniqueId ? { ...f, onlyShowPlaidOption } : f
                            )
                          );
                        };

                        let displayValue = '';
                        if (field.fromField === 'DATE') {
                          displayValue = field?.value
                            ? moment(field.value, ['MM-DD-YYYY', moment.ISO_8601]).format(
                                'MM-DD-YYYY'
                              )
                            : '';
                        } else if (field.fromField === 'CHECK_BOX') {
                          displayValue = field?.value ? 'True' : 'False';
                        } else if (typeof field.value === 'object' && field.value !== null) {
                          displayValue = field.value.name ?? '';
                        } else if (field.fromField === 'CURRENCY') {
                          displayValue = formatMoney(field.value, 2);
                        } else {
                          displayValue = field?.value ?? '';
                        }

                        return (
                          <div
                            key={field.uniqueId + index}
                            className={twMerge(
                              'flex flex-col rounded-lg border cursor-pointer transition-all select-none',
                              isSelected
                                ? 'border-blue-400 bg-blue-50 shadow-sm'
                                : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                            )}
                            onClick={() => handleSelect()}>
                            <div className="flex items-center gap-2.5 p-2.5">
                              <DillCheckbox isChecked={isSelected} />
                              <div className="min-w-0 flex-1">
                                <div className="text-xs font-medium text-gray-800 truncate">
                                  {field.name}
                                </div>
                                {displayValue !== '' && (
                                  <div className="text-xs text-gray-400 truncate">
                                    {displayValue}
                                  </div>
                                )}
                              </div>
                            </div>
                            {field.id.includes('bank-reference') && isSelected && (
                              <div
                                className="flex items-center gap-2 mx-2.5 mb-2.5 px-2.5 py-2 bg-blue-100 rounded-md cursor-pointer"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSelectPlaid(field.uniqueId, !plaidChecked);
                                }}>
                                <DillCheckbox isChecked={plaidChecked} />
                                <span className="text-xs text-blue-700 font-medium">
                                  Show Plaid option only
                                </span>
                              </div>
                            )}
                          </div>
                        );
                      }
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </DillModal>
  );
};

export default CreditApplicationFeedbackModal;
